import React, { useState } from 'react';
import { find, map, split, trim } from 'lodash';

import { Typography, Grid, Skeleton } from '@mui/material';
import { Carousel } from 'react-responsive-carousel';
import { Button, Dropdown, Modal } from '../../components';
import { StyledModalWrapper } from './styles';

import 'react-responsive-carousel/lib/styles/carousel.min.css';

import {
  irregularitiesSlice,
  paymentMethodsSlice,
  useDispatch,
  useSelector
} from '../../stores';

import { CreatePaymentMethodModal } from '../PaymentMethods/modals/CreatePaymentMethodModal';

import { handleDateTime, handleMoney, hasPhoto } from '../../utils';
import useWindowDimensions from '../../utils/useWindowDimensions';

export const IrregularityListDetails = () => {
  const dispatch = useDispatch();

  const { browserWidth } = useWindowDimensions();
  const isMobile = browserWidth < 769;

  const { cpf } = useSelector((state) => state.profileReducer);
  const { paymentMethods } = useSelector((state) => state.paymentMethodsReducer);
  const { irregularitiesLoading, irregularityListDetails } = useSelector((state) => state.irregularitiesReducer);

  const [paymentMethodText, setPaymentMethodText] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<
    | {
        id: number;
        lastFour: string;
        brand: string;
        bankId: number | null;
        default: 0 | 1;
      }
    | 'PIX'
    | undefined
  >(undefined);

  const [error, setError] = useState<string | undefined>();

  const [isCarouselModalOpen, setIsCarouselModalOpen] = useState<boolean>(false);

  const handleSelectedPaymentMethod = (e: string) => {
    if (e !== 'PIX') {
      const paymentMethod = find(paymentMethods, [
        'lastFour',
        trim(split(e.toString(), ' ')[1]),
      ]);
      setPaymentMethod(paymentMethod);
    } else {
      setPaymentMethod('PIX');
    }
    setPaymentMethodText(e.toString());
  };

  const handleSubmit = () => {
    if (!paymentMethod) {
      setError('Escolha um meio de pagamento');
    } else {
      setError(undefined);

      if (irregularityListDetails) {
        if (paymentMethod === 'PIX') {
          dispatch(irregularitiesSlice.actions.setPaymentMethod('PIX'));

          if (!cpf) {
            dispatch(
              irregularitiesSlice.actions.toggleEditCPFIrregularitiesIsOpen(),
            );
          } else {
            dispatch(irregularitiesSlice.actions.toggleRegularizeListIsOpen());
          }
        } else {
          dispatch(irregularitiesSlice.actions.setPaymentMethod(paymentMethod));
          dispatch(irregularitiesSlice.actions.toggleRegularizeListIsOpen());
        }
      }
    }
  };

  const handleCarouselList = () => {
    const list: React.JSX.Element[] = [];
    map(irregularityListDetails?.irregularities, (item) => {
      if (hasPhoto(item.photoLocation)) {
        list.push(
          <div key={`local-${item.id}`} style={{ padding: '10px' }}>
            <img
              alt='local'
              src={item.photoLocation}
              style={{ borderRadius: '10px' }}
            />
          </div>,
        );
      }
      list.push(
        <div key={`placa-${item.id}`} style={{ padding: '10px' }}>
          <img
            alt='placa'
            src={item.photoPlate}
            style={{ borderRadius: '10px' }}
          />
        </div>,
      );
    });
    return list;
  };

  return irregularitiesLoading ? (
    <>
      <Skeleton width='100%' height={30} />
      <Skeleton variant='rectangular' width='100%' height={118} />
      <Skeleton width='60%' />
      <Skeleton width='45%' />
      <Skeleton width='80%' />
      <Skeleton width='75%' />
      <Skeleton width='60%' />
    </>
  ) : (
    <>
      {isCarouselModalOpen && (
        <Modal
          open={isCarouselModalOpen}
          onClose={() => setIsCarouselModalOpen(false)}
          title=''
        >
          <StyledModalWrapper>
            <Carousel
              showStatus={false}
              showThumbs={false}
              showIndicators={false}
            >
              {handleCarouselList()}
            </Carousel>
          </StyledModalWrapper>
        </Modal>
      )}
      <Grid
        container
        rowSpacing={1}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          marginTop: {
            xs: '20px',
            sm: 0,
          },
        }}
      >
        <Grid
          item
          xs={12}
          marginBottom={{
            sm: 3,
          }}
          sx={{
            textAlign: 'center',
            width: '100%',
            maxWidth: '300px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <Typography
            color='primary'
            sx={{
              fontSize: { xs: '4.5vw', sm: '20px' },
              fontWeight: 'bold',
            }}
          >
            Detalhes da(s) irregularidade(s){' '}
            {map(irregularityListDetails?.irregularities, (item, index) => {
              if (
                irregularityListDetails?.irregularities.length &&
                index === irregularityListDetails?.irregularities.length - 1
              ) {
                return `${item.id.toString()}`;
              } else {
                return `${item.id.toString()}, `;
              }
            })}
          </Typography>
        </Grid>
        <Grid container xs={12} sm={6} md={5} justifyContent='center' sx={{ padding: 1 }}>
          {isMobile && (
            <img
              style={{ cursor: 'pointer', borderRadius: 8 }}
              src={irregularityListDetails?.irregularities[0].photoPlate}
              onClick={() => setIsCarouselModalOpen(true)}
            />
          )}
          {!isMobile && (
            <Carousel
              showStatus={false}
              showThumbs={false}
              showIndicators={false}
            >
              {handleCarouselList()}
            </Carousel>
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={7}>
          <Grid xs={12} margin={1}>
            <Typography
              color='primary'
              sx={{ fontSize: { xs: '14px', sm: '16px' } }}
            >
              <b>Placa:</b> {irregularityListDetails?.payment?.licensePlate}
            </Typography>
          </Grid>
          <Grid xs={12} margin={1}>
            <Typography
              color='primary'
              sx={{ fontSize: { xs: '14px', sm: '16px' } }}
            >
              <b>Entrada:</b>{' '}
              {irregularityListDetails?.payment?.entryDateTime &&
                handleDateTime(irregularityListDetails?.payment?.entryDateTime)}
            </Typography>
          </Grid>
          <Grid xs={12} margin={1}>
            <Typography
              color='primary'
              sx={{ fontSize: { xs: '14px', sm: '16px' } }}
            >
              <b>Saída:</b>{' '}
              {irregularityListDetails?.payment?.exitDateTime &&
                handleDateTime(irregularityListDetails?.payment?.exitDateTime)}
            </Typography>
          </Grid>
          <Grid xs={12} margin={1}>
            <Typography
              color='primary'
              sx={{ fontSize: { xs: '14px', sm: '16px' } }}
            >
              <b>Período:</b>{' '}
              {irregularityListDetails?.payment?.stayTime &&
                irregularityListDetails?.payment?.stayTime}
            </Typography>
          </Grid>
          <Grid xs={12} margin={1}>
            <Typography
              color='primary'
              sx={{ fontSize: { xs: '14px', sm: '16px' } }}
            >
              <b>Valor:</b>{' '}
              {irregularityListDetails?.payment?.price &&
                handleMoney(irregularityListDetails?.payment?.price)}
            </Typography>
          </Grid>
          <Grid
            xs={12}
            rowSpacing={3}
            margin={1}
            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Dropdown
              options={[
                'PIX',
                ...map(paymentMethods, (item) => {
                  return `**** ${item.lastFour} (${item.brand})`;
                }),
              ]}
              data-testid='paymentMethod'
              margin='normal'
              label='Meio de pagamento'
              type='text'
              value={paymentMethodText}
              onChange={handleSelectedPaymentMethod}
              errorMessage={error}
            />
          </Grid>
          <Grid
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button
              variant='text'
              label='Cadastrar meio de pagamento'
              type='submit'
              onClick={() => {
                dispatch(
                  paymentMethodsSlice.actions.toggleCreatePaymentMethodIsOpen(),
                );
                // navigate(PAYMENT_METHODS);
              }}
              color='primary'
              style={{ width: '100%', marginTop: 10 }}
            />
            <Button
              variant='contained'
              label='Pagar irregularidades'
              onClick={handleSubmit}
              color='secondary'
              loading={irregularitiesLoading}
              style={{ width: '100%', marginTop: 10 }}
            />
            <Button
              label='Voltar'
              type='submit'
              onClick={() => {
                dispatch(
                  irregularitiesSlice.actions.resetIrregularityListDetails(),
                );
                dispatch(irregularitiesSlice.actions.toggleShowListDetails());
              }}
              color='primary'
              style={{ width: '100%', marginTop: 10 }}
            />
          </Grid>
        </Grid>
      </Grid>
      <CreatePaymentMethodModal />
    </>
  );
};